import { defineStore } from 'pinia';
import { ref } from 'vue';
import api from '../services/api';

interface CachedSnapshot {
  url: string;
  fetchedAt: number;
}

const SNAPSHOT_TTL_MS = 30_000;
const SNAPSHOT_FETCH_CONCURRENCY = 4;
const SNAPSHOT_FETCH_STAGGER_MS = 75;
const SNAPSHOT_RETRY_DELAYS_MS = [250];

type SnapshotError = Error & {
  status?: number;
};

interface FetchSnapshotsOptions {
  force?: boolean;
  concurrency?: number;
  staggerMs?: number;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function isRetryableSnapshotError(error: unknown): boolean {
  const snapshotError = error as SnapshotError | undefined;
  const status = snapshotError?.status;
  if (status && [400, 401, 403, 404, 409].includes(status)) {
    return false;
  }

  if (status === 503) {
    return true;
  }

  const message = snapshotError?.message?.toLowerCase() ?? '';
  return [
    'timeout',
    'failed to fetch',
    'networkerror',
    'request channel not available',
    'webrtc connection closed',
    'binary download stalled',
    'empty snapshot response',
  ].some((term) => message.includes(term));
}

function normalizeCameraId(cameraId: unknown): string {
  return String(cameraId ?? '').trim();
}

export const useCameraSnapshotsStore = defineStore('cameraSnapshots', () => {
  const snapshots = ref<Record<string, CachedSnapshot>>({});
  const inFlight = new Map<string, Promise<string | null>>();
  /** Ids raised since the last flush — see `queueSnapshot`. */
  const queuedIds = new Set<string>();
  let queueFlushTimer: ReturnType<typeof setTimeout> | null = null;

  function getSnapshotUrl(cameraId: unknown): string | null {
    const id = normalizeCameraId(cameraId);
    if (!id) return null;
    return snapshots.value[id]?.url ?? null;
  }

  function isFresh(cameraId: string): boolean {
    const snapshot = snapshots.value[cameraId];
    return Boolean(snapshot && Date.now() - snapshot.fetchedAt < SNAPSHOT_TTL_MS);
  }

  async function requestSnapshot(cameraId: string): Promise<string | null> {
    const previousUrl = snapshots.value[cameraId]?.url ?? null;

    for (let attempt = 0; attempt <= SNAPSHOT_RETRY_DELAYS_MS.length; attempt += 1) {
      try {
        const response = await api.get(`/cameras/${encodeURIComponent(cameraId)}/snapshot`, {
          responseType: 'blob',
          requestClass: 'snapshot',
        });
        const blob = response.data;

        if (!blob || blob.size === 0) {
          const error = new Error('Empty snapshot response') as SnapshotError;
          error.status = 503;
          throw error;
        }

        const nextUrl = URL.createObjectURL(blob);
        const previousSnapshotUrl = snapshots.value[cameraId]?.url;
        snapshots.value[cameraId] = { url: nextUrl, fetchedAt: Date.now() };
        if (previousSnapshotUrl && previousSnapshotUrl !== nextUrl) {
          URL.revokeObjectURL(previousSnapshotUrl);
        }
        return nextUrl;
      } catch (error) {
        const shouldRetry = attempt < SNAPSHOT_RETRY_DELAYS_MS.length && isRetryableSnapshotError(error);
        if (!shouldRetry) {
          break;
        }
        await delay(SNAPSHOT_RETRY_DELAYS_MS[attempt]);
      }
    }

    return previousUrl;
  }

  async function fetchSnapshot(cameraId: unknown, force = false): Promise<string | null> {
    const id = normalizeCameraId(cameraId);
    if (!id) return null;

    if (!force && isFresh(id)) return snapshots.value[id].url;
    if (inFlight.has(id)) return inFlight.get(id)!;

    const request = requestSnapshot(id).finally(() => {
      inFlight.delete(id);
    });

    inFlight.set(id, request);
    return request;
  }

  async function fetchSnapshots(cameraIds: unknown[], options: FetchSnapshotsOptions = {}): Promise<void> {
    const ids = Array.from(new Set(cameraIds.map(normalizeCameraId).filter(Boolean)));
    if (ids.length === 0) {
      return;
    }

    const concurrency = Math.max(1, Math.min(options.concurrency ?? SNAPSHOT_FETCH_CONCURRENCY, ids.length));
    const staggerMs = Math.max(0, options.staggerMs ?? SNAPSHOT_FETCH_STAGGER_MS);
    let nextIndex = 0;

    await Promise.all(
      Array.from({ length: concurrency }, async (_, workerIndex) => {
        if (workerIndex > 0 && staggerMs > 0) {
          await delay(workerIndex * staggerMs);
        }

        while (true) {
          const currentIndex = nextIndex;
          nextIndex += 1;
          if (currentIndex >= ids.length) {
            return;
          }

          await fetchSnapshot(ids[currentIndex], options.force);
        }
      }),
    );
  }

  /**
   * Ask for a camera's snapshot without knowing who else is asking.
   *
   * Camera tiles mount together (a security page grids every camera, a room
   * opens several), and each one used to fire its own request on mount. On
   * HTTP/1.1 that saturates the per-host connection pool and leaves everything
   * else — including the page's own data — queued behind it. Requests raised in
   * the same tick are collected here and handed to `fetchSnapshots`, which owns
   * the concurrency cap and the stagger (the rule `hubConnect.ts` already
   * applies when it avoids resuming on the cameras page).
   */
  function queueSnapshot(cameraId: unknown): void {
    const id = normalizeCameraId(cameraId);
    if (!id || isFresh(id) || inFlight.has(id)) return;

    queuedIds.add(id);
    if (queueFlushTimer !== null) return;

    queueFlushTimer = setTimeout(() => {
      queueFlushTimer = null;
      const ids = Array.from(queuedIds);
      queuedIds.clear();
      if (ids.length > 0) void fetchSnapshots(ids);
    }, 0);
  }

  function clear(cameraId?: unknown) {
    if (cameraId !== undefined) {
      const id = normalizeCameraId(cameraId);
      const snapshot = snapshots.value[id];
      if (snapshot?.url) URL.revokeObjectURL(snapshot.url);
      delete snapshots.value[id];
      inFlight.delete(id);
      queuedIds.delete(id);
      return;
    }

    for (const snapshot of Object.values(snapshots.value)) {
      URL.revokeObjectURL(snapshot.url);
    }
    snapshots.value = {};
    inFlight.clear();
    queuedIds.clear();
    if (queueFlushTimer !== null) {
      clearTimeout(queueFlushTimer);
      queueFlushTimer = null;
    }
  }

  /**
   * Drop all cached snapshot blob URLs when switching to a different hub.
   */
  function resetForHubSwitch() {
    clear();
  }

  return {
    snapshots,
    getSnapshotUrl,
    fetchSnapshot,
    fetchSnapshots,
    queueSnapshot,
    clear,
    resetForHubSwitch,
  };
});
